import useStyles from 'isomorphic-style-loader/useStyles';
import {
  Modal as SemanticModal,
  type ModalProps as SemanticModalProps,
  ModalActions as SemanticModalActions,
  ModalContent as SemanticModalContent,
  ModalDescription as SemanticModalDescription,
  ModalDimmer as SemanticModalDimmer,
  ModalHeader as SemanticModalHeader,
} from 'semantic-ui-react';

import type { ModalData, ModalType } from '@models';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  hideModal,
  selectActiveModal,
  selectActiveModalProps,
  selectIsModalVisible,
  selectIsMultipleActiveModals,
  showModal,
} from '@store/modals.slice';

import s from './modal.scss';

export interface UseModalReturn<
  Type extends ModalType,
  Data extends ModalData<Type>,
> {
  id: Type;
  data?: Data;
  isVisible: boolean;
  isOpen: boolean;
  open: (props: Data extends void | null ? void : Data) => void;
  close: () => void;
}

/**
 * A hook to manage the state of a modal in the Redux store. Returns an object
 * containing the current state, data and methods to open and close the modal.
 */
export const useModal = <Type extends ModalType, Data extends ModalData<Type>>(
  type: Type,
): UseModalReturn<Type, Data> => {
  const dispatch = useAppDispatch();
  const instance = useAppSelector(selectActiveModal(type));
  const data = useAppSelector(selectActiveModalProps<Type, Data>(type));
  const isVisible = useAppSelector(selectIsModalVisible(type));

  return {
    id: type,
    data,
    isVisible,
    isOpen: !!instance,
    open: (props?: Data | void) => dispatch(showModal({ type, props })),
    close: () => dispatch(hideModal(type)),
  };
};

export interface ModalProps<T extends ModalType> extends SemanticModalProps {
  /**
   * A unique id for the modal instance. This should be unique across all modals
   * in the application, and will be used to manage the modal's state and data
   * in the Redux store.
   */
  id: T;
  /**
   * Stackable modals remain visible behind other modals that are launched on
   * top of them.
   */
  stackable?: boolean;
}

const Modal = <T extends ModalType>({
  id,
  children,
  onClose,
  stackable,
  ...props
}: ModalProps<T>) => {
  useStyles(s);

  const { isVisible, isOpen, close } = useModal(id);

  const isManyActive = useAppSelector(selectIsMultipleActiveModals);

  return (
    <SemanticModal
      id={id}
      role="dialog"
      className={!isVisible && !stackable ? s.hiddenModal : ''}
      style={{ visibility: isVisible ? 'visible' : 'hidden' }}
      open={isOpen}
      closeIcon={isVisible}
      dimmer={
        isManyActive && isVisible
          ? // If there are multiple active modals, apply a dimmer to the topmost
            // instance in case there are stackable instances underneath.
            true
          : // Otherwise this dimmer is transparent (i.e. use root dimmer)
            { style: { background: 'transparent' } }
      }
      onClose={(event, data) => {
        close();
        onClose?.(event, data);
      }}
      mountNode={process.env.BROWSER && document?.getElementById('rootDimmer')}
      {...props}
    >
      {children}
    </SemanticModal>
  );
};

export default Object.assign(Modal, {
  Actions: SemanticModalActions,
  Content: SemanticModalContent,
  Description: SemanticModalDescription,
  Dimmer: SemanticModalDimmer,
  Header: SemanticModalHeader,
});
